import React from 'react';
import { getStrategyMeta } from './shared/strategyMeta';
import { SinglePositionCycleContext as CycleContext } from '../api/singlePositionCycles';
import './SinglePositionCycleContext.css';

interface Props {
  context: CycleContext | null;
  tradeGroupType: string;
}

/** camelCase → "Title Case" label (e.g. entryFastMa → "Entry Fast Ma"). */
function humanize(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\bPct\b/gi, '%')
    .trim();
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return String(value);
}

/**
 * Per-cycle strategy context (spec REQ-011): renders the type-specific fields of the discriminated
 * `context` union first-class. Generic over all 8 covered types — it renders every field the context
 * carries (nulls tolerated), titled with the strategy's shared label/icon (getStrategyMeta).
 */
const SinglePositionCycleContext: React.FC<Props> = ({ context, tradeGroupType }) => {
  const meta = getStrategyMeta(tradeGroupType);
  const Icon = meta.Icon;

  const entries = context
    ? Object.entries(context).filter(([key]) => key !== 'type')
    : [];

  return (
    <div className={`spc-context ${meta.cardClass}`}>
      <div className="spc-context-header">
        <Icon size={16} className="spc-context-icon" />
        <span className="spc-context-title">{meta.label} Context</span>
      </div>
      {entries.length === 0 ? (
        <div className="spc-context-empty">No strategy context recorded for this cycle.</div>
      ) : (
        <div className="spc-context-grid">
          {entries.map(([key, value]) => (
            <div key={key} className="spc-context-field">
              <span className="spc-context-label">{humanize(key)}</span>
              <span className="spc-context-value">{formatValue(value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SinglePositionCycleContext;
